import { useState } from 'react';

import type { AddToGroupPayload } from '@/components/AddToGroupModal/types';
import { useTreatmentGroupMembershipActions } from '@/hooks/useTreatmentGroupMemberships';
import { TreatmentGroupDetailForPerson, TreatmentGroupForPerson } from '@/types/treatmentGroup';

type Group = TreatmentGroupDetailForPerson | TreatmentGroupForPerson;

interface ManageState {
  confirmRemoveMessage: string;
  group: Group;
  initialSessionIds: string[];
  membershipId: string;
}

// Wraps enroll so a group whose appointment type has a relationship_type, or
// that has sessions to choose between, opens AddToGroupModal for provider
// and/or session selection first; a group with neither keeps the existing
// one-click enroll. Also wraps managing an already-enrolled member's
// session selection (and removing them) through the same modal.
export const useAddToGroupFlow = (personId: string, timeZone?: string) => {
  const membershipActions = useTreatmentGroupMembershipActions();
  const [pendingGroup, setPendingGroup] = useState<Group | null>(null);
  const [manageState, setManageState] = useState<ManageState | null>(null);

  const startEnroll = (group: Group) => {
    if (group.relationship_type || group.sessions.length > 0) {
      setPendingGroup(group);
      return;
    }
    membershipActions.enroll(personId, group.id);
  };

  const startManage = (
    group: Group,
    membershipId: string,
    initialSessionIds: string[],
    confirmRemoveMessage: string
  ) => setManageState({ confirmRemoveMessage, group, initialSessionIds, membershipId });

  const closeModal = () => {
    setPendingGroup(null);
    setManageState(null);
  };

  const confirmEnroll = (payload: AddToGroupPayload) => {
    if (!pendingGroup) return;
    membershipActions.enroll(personId, pendingGroup.id, payload);
    closeModal();
  };

  const saveManagedSessions = (partialSessionLetters: string[]) => {
    if (!manageState) return;
    membershipActions.updateSessions(
      manageState.membershipId,
      manageState.group.id,
      partialSessionLetters
    );
    closeModal();
  };

  const removeManagedMembership = () => {
    if (!manageState) return;
    membershipActions.remove({
      membershipId: manageState.membershipId,
      treatmentGroupId: manageState.group.id,
      confirmMessage: manageState.confirmRemoveMessage
    });
    closeModal();
  };

  const modal =
    (pendingGroup && {
      group: pendingGroup,
      isPending: membershipActions.isPending,
      onClose: closeModal,
      onConfirm: confirmEnroll,
      open: true,
      timeZone
    }) ||
    (manageState && {
      group: manageState.group,
      isPending: membershipActions.isPending,
      manage: {
        initialSessionIds: manageState.initialSessionIds,
        onRemove: removeManagedMembership,
        onSave: saveManagedSessions
      },
      onClose: closeModal,
      onConfirm: () => {},
      open: true,
      timeZone
    }) ||
    null;

  return {
    isPending: membershipActions.isPending,
    modal,
    remove: membershipActions.remove,
    startEnroll,
    startManage
  };
};

export default useAddToGroupFlow;
